export type PlatformLanguage = "ar" | "en";

export type PlatformDiagnostic = {
  message: string;
  code: string | null;
  correlationId: string | null;
};

type DiagnosticScope = "platform" | "administrators" | "sessions" | "audit";
type ProblemRecord = Record<string, unknown>;

const localizedMessages: Record<string, { ar: string; en: string }> = {
  PLATFORM_ADMIN_EMAIL_INVALID: {
    ar: "أدخل عنوان بريد صالحًا لحساب Leylion.",
    en: "Enter a valid email address for an existing Leylion account."
  },
  PLATFORM_ADMIN_REASON_INVALID: {
    ar: "السبب مطلوب ويجب ألا يتجاوز 160 حرفًا.",
    en: "A reason is required and must not exceed 160 characters."
  },
  PLATFORM_ADMIN_USER_NOT_FOUND: {
    ar: "لم يتم العثور على حساب Leylion المرتبط بهذا الطلب.",
    en: "The Leylion account linked to this request was not found."
  },
  PLATFORM_ADMIN_USER_INACTIVE: {
    ar: "يجب أن يكون حساب Leylion فعّالًا قبل منحه صلاحية PlatformOwner.",
    en: "The Leylion account must be active before PlatformOwner access can be granted."
  },
  PLATFORM_ADMIN_MFA_REQUIRED: {
    ar: "يجب إعداد المصادقة متعددة العوامل للحساب قبل منحه صلاحية PlatformOwner.",
    en: "MFA must be configured before PlatformOwner access can be granted."
  },
  PLATFORM_ADMIN_MEMBERSHIP_EXISTS: {
    ar: "يمتلك هذا الحساب عضوية PlatformOwner بالفعل. استخدم إجراءات دورة العضوية الحالية.",
    en: "This account already has a PlatformOwner membership. Use its existing lifecycle actions."
  },
  PLATFORM_ADMIN_MEMBERSHIP_NOT_FOUND: {
    ar: "لم تعد عضوية PlatformOwner المطلوبة موجودة. حدّث القائمة وحاول مرة أخرى.",
    en: "The requested PlatformOwner membership no longer exists. Refresh and try again."
  },
  PLATFORM_ADMIN_STALE_STATE: {
    ar: "تغيّرت العضوية بعد تحميلها. حدّث البيانات ثم أعد المحاولة.",
    en: "The membership changed after it was loaded. Refresh and try again."
  },
  PLATFORM_LAST_OWNER_REQUIRED: {
    ar: "يجب أن يبقى مالك منصة فعّال واحد على الأقل. أضف أو فعّل مالكًا آخر قبل هذا الإجراء.",
    en: "At least one active PlatformOwner must remain. Add or reactivate another owner first."
  },
  PLATFORM_ADMIN_TRANSITION_INVALID: {
    ar: "انتقال حالة عضوية PlatformOwner المطلوب غير مسموح.",
    en: "The requested PlatformOwner membership transition is not allowed."
  },
  PLATFORM_SESSION_NOT_FOUND: {
    ar: "لم يتم العثور على جلسة المنصة المطلوبة. حدّث الجلسات وحاول مرة أخرى.",
    en: "The requested platform session was not found. Refresh the sessions and try again."
  },
  PLATFORM_AUDIT_RANGE_INVALID: {
    ar: "يجب ألا يكون وقت بداية سجل التدقيق بعد وقت النهاية.",
    en: "The audit range start must not be after the range end."
  },
  PLATFORM_AUDIT_CURSOR_INVALID: {
    ar: "انتهت صلاحية صفحة سجل التدقيق أو أصبحت غير صالحة. أعد تحميل النتائج من البداية.",
    en: "The audit page cursor is invalid or expired. Reload the results from the beginning."
  },
  PLATFORM_AUDIT_ACTION_INVALID: {
    ar: "مرشح الإجراء في سجل التدقيق غير صالح.",
    en: "The audit action filter is invalid."
  },
  PLATFORM_AUDIT_ENTITY_TYPE_INVALID: {
    ar: "مرشح نوع الكيان في سجل التدقيق غير صالح.",
    en: "The audit entity-type filter is invalid."
  },
  PLATFORM_AUDIT_ENTITY_ID_INVALID: {
    ar: "مرشح معرّف الكيان في سجل التدقيق غير صالح.",
    en: "The audit entity-ID filter is invalid."
  },
  SESSION_INVALID: {
    ar: "انتهت جلسة المنصة أو تم إلغاؤها. سجّل الدخول مرة أخرى للمتابعة.",
    en: "The platform session expired or was revoked. Sign in again to continue."
  },
  INTERNAL_ERROR: {
    ar: "حدث خطأ غير متوقع. استخدم معرّف الارتباط عند التواصل مع الدعم.",
    en: "An unexpected error occurred. Use the correlation ID when contacting support."
  }
};

function isRecord(value: unknown): value is ProblemRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(record: ProblemRecord, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function extractCode(problem: ProblemRecord | null): string | null {
  if (!problem) return null;
  const direct = readString(problem, "code") ?? readString(problem, "title");
  if (direct) return direct;
  const type = readString(problem, "type");
  if (!type) return null;
  const segment = type.split("/").filter(Boolean).at(-1);
  return segment?.trim() || null;
}

function fallbackMessage(language: PlatformLanguage, scope: DiagnosticScope, status: number | null): string {
  const ar = language === "ar";
  if (status === 401) {
    return ar
      ? "انتهت جلسة المنصة أو تم إلغاؤها. سجّل الدخول مرة أخرى للمتابعة."
      : "The platform session expired or was revoked. Sign in again to continue.";
  }
  if (status === 403) {
    return ar
      ? "لا تملك الجلسة الحالية صلاحية تنفيذ هذا الطلب في Control Plane."
      : "The current session is not authorized to perform this control-plane request.";
  }

  const messages: Record<DiagnosticScope, { ar: string; en: string }> = {
    platform: {
      ar: "تعذر إكمال طلب المنصة. حاول مرة أخرى، واستخدم معرّف الارتباط عند استمرار المشكلة.",
      en: "The platform request could not be completed. Retry, and use the correlation ID if the problem persists."
    },
    administrators: {
      ar: "تعذر إكمال إجراء إدارة ملاك المنصة. راجع البيانات وحاول مرة أخرى.",
      en: "The PlatformOwner administration action could not be completed. Review the input and try again."
    },
    sessions: {
      ar: "تعذر إكمال إجراء جلسات المنصة. حدّث الجلسات وحاول مرة أخرى.",
      en: "The platform-session action could not be completed. Refresh the sessions and try again."
    },
    audit: {
      ar: "تعذر تحميل سجل تدقيق المنصة. بقية لوحة المنصة ما زالت متاحة.",
      en: "The platform audit trail could not be loaded. The rest of the Platform Console remains available."
    }
  };
  return messages[scope][language];
}

export function toPlatformDiagnostic(
  caught: unknown,
  language: PlatformLanguage,
  scope: DiagnosticScope = "platform"
): PlatformDiagnostic {
  const errorRecord = isRecord(caught) ? caught : null;
  const problem = errorRecord && isRecord(errorRecord.problem) ? errorRecord.problem : null;
  const statusValue = errorRecord?.status;
  const status = typeof statusValue === "number" ? statusValue : null;
  const code = extractCode(problem);
  const correlationId = problem ? readString(problem, "correlationId") : null;
  const localized = code ? localizedMessages[code] : undefined;

  return {
    message: localized?.[language] ?? fallbackMessage(language, scope, status),
    code,
    correlationId
  };
}
